import type { Ton } from '../commun.js'

/**
 * HUB DE SUPERVISION ET MODE SOIRÉE — le modèle pur (21/10/2026).
 *
 * Les formes de Sonde lues par le hub (`overview`, `zones`, `incidents`,
 * `discovery`), relayées TELLES QUELLES par Gestion, et les quelques règles
 * d'affichage qui en découlent. Aucune dépendance à React : tout ce qui décide
 * d'une couleur ou d'un ordre est ici, et testé (`tests/sonde-hub.test.ts`).
 *
 * Règles d'honnêteté (§7.5) portées par ce module :
 *   · « éteinte comme prévu » n'est JAMAIS une panne : ton neutre `off` ;
 *   · on ne sait pas (pas de donnée, collecteur figé, salle vide) = gris ;
 *   · une valeur absente reste `null` jusqu'à l'écran, qui écrit « — » ;
 *   · `unobserved` n'est jamais compté en panne ;
 *   · un chemin produit par Sonde n'est suivi que s'il reste dans
 *     `/infrastructure` (jamais une URL externe, jamais `..`).
 */

/* ═══════════════ Formes de Sonde (contrat v1, sous-ensemble lu ici) ═══════════════ */

export type Niveau = 'OPERATIONAL' | 'ATTENTION' | 'DEGRADED'

export interface Comptes {
  total: number; online: number; degraded: number; offline: number; off_expected: number
  maintenance: number; unknown: number; stale: number; unavailable: number
}

export interface FenetreSonde {
  origin: 'gestion' | 'manual' | 'fallback' | string
  label: string | null
  starts_at: string
  ends_at: string
  operating_from?: string
  operating_until?: string
}

export interface AlimSonde {
  state: 'on' | 'off' | 'unknown' | string
  since: string | null
  label_fr: string
  situation: 'on' | 'off_as_planned' | 'off_unexpected' | 'mains_outage' | 'unknown' | string
  source: 'collector' | 'snapshot' | 'unavailable' | string
}

export interface ZoneSonde {
  code: string
  label: string
  kind: string
  power: AlimSonde
  scheduled: boolean
  operating: boolean
  window: FenetreSonde | null
  next_window: FenetreSonde | null
  score: number | null
  score_detail?: { explanation_fr?: string | null } | null
  level: Niveau | null
  counts: Comptes
  incidents_open: number
  data_age_s: number | null
}

export interface CollecteurSonde {
  ok: boolean
  stale: boolean
  heartbeat_age_s: number | null
  code: string | null
  detail_fr: string | null
}

export interface AttentionSonde {
  kind: string
  severity: 'info' | 'warning' | 'error' | 'critical' | string
  title_fr: string
  detail_fr: string | null
  ref: string | null
  zone_code: string | null
  since: string | null
  count: number | null
  path: string | null
  shadow?: boolean
}

export interface CompteurDirect { ok: number | null; total: number | null; unobserved: number | null; data_age_s: number | null }

export interface SonDirect {
  uid: string
  zone_code: string | null
  laeq15_db: number | null
  limit_a_db: number | null
  minutes_to_limit: number | null
  at: string | null
  data_age_s: number | null
}

export interface FamilleSonde {
  family: string
  label: string
  level: Niveau | null
  counts: Comptes
  weighted_total: number
  attention: number
  data_age_s: number | null
}

export interface SourceOmbre { source: string; label: string; owner: 'gestion' | 'sonde' | null; sonde_publishes: boolean }

export interface OverviewSonde {
  generated_at: string
  shadow_mode: boolean
  shadow_release?: string
  shadow_sources?: SourceOmbre[] | null
  shadow_summary_fr?: string
  mode: 'evening' | 'rest' | null
  operating_mode_setting: string
  collector: CollecteurSonde
  level: Niveau
  level_label: string
  level_reasons: string[]
  data_age_s: number | null
  weighted: Comptes
  devices_total: number
  families: FamilleSonde[]
  zones: ZoneSonde[]
  attention: { total: number; items: AttentionSonde[] }
  sources: { total: number; ok: number; degraded: number; unavailable: number; disabled: number; not_configured: number }
  incidents: { open: number; critical: number }
  mains_outage: { active: boolean; since: string | null; affected: number | null; runtime_min: number | null; data_age_s: number | null }
  alerts: { firing: number; shadow_firing: number }
  live: {
    payment_terminals: CompteurDirect
    cameras_recording: CompteurDirect
    dmx_ports_active: CompteurDirect
    sound: SonDirect[]
  }
}

export interface ZonesSonde { generated_at: string; collector: CollecteurSonde; zones: ZoneSonde[] }

export interface IncidentSonde {
  id: string
  status: 'open' | 'acknowledged' | 'recovering' | 'resolved' | string
  status_label: string
  severity: 'info' | 'warning' | 'error' | 'critical' | string
  title: string
  kind: string | null
  cause: string | null
  root: { uid: string; display_name: string; kind: string; zone_code: string | null } | null
  zone_code: string | null
  affected_count: number
  opened_at: string
  acked_at: string | null
  recovering_at: string | null
  duration_s: number
  notified: boolean
  /** Sourdine (contrat 593166c) : null si aucune, échue ou incident résolu. */
  snoozed_until?: string | null
}

export interface IncidentsSonde {
  items: IncidentSonde[]
  counts: { open: number; acknowledged: number; recovering: number; resolved_7d: number }
  next_cursor: string | null
}

export interface DecouvertesSonde { counts: { pending: number; accepted: number; ignored: number } }

/* ═══════════════ Gardes de forme ═══════════════ */

const objet = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)
const nombre = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x)

function estZone(x: unknown): x is ZoneSonde {
  return objet(x) && typeof x['code'] === 'string' && typeof x['label'] === 'string'
    && objet(x['power']) && objet(x['counts']) && nombre((x['counts'] as Record<string, unknown>)['total'])
}

function estCollecteur(x: unknown): x is CollecteurSonde {
  return objet(x) && typeof x['ok'] === 'boolean' && typeof x['stale'] === 'boolean'
}

export function estOverview(x: unknown): x is OverviewSonde {
  if (!objet(x) || typeof x['level'] !== 'string' || typeof x['level_label'] !== 'string') return false
  if (!Array.isArray(x['level_reasons']) || !estCollecteur(x['collector'])) return false
  if (!Array.isArray(x['zones']) || !(x['zones'] as unknown[]).every(estZone)) return false
  if (!Array.isArray(x['families'])) return false
  const a = x['attention'], l = x['live']
  if (!objet(a) || !Array.isArray(a['items'])) return false
  if (!objet(l) || !objet(l['payment_terminals']) || !objet(l['cameras_recording']) || !objet(l['dmx_ports_active']) || !Array.isArray(l['sound'])) return false
  return objet(x['incidents']) && objet(x['sources']) && objet(x['mains_outage']) && objet(x['weighted'])
}

export function estZones(x: unknown): x is ZonesSonde {
  return objet(x) && Array.isArray(x['zones']) && (x['zones'] as unknown[]).every(estZone) && estCollecteur(x['collector'])
}

export function estIncidents(x: unknown): x is IncidentsSonde {
  return objet(x) && Array.isArray(x['items']) && objet(x['counts'])
    && (x['items'] as unknown[]).every((i) => objet(i) && typeof i['id'] === 'string' && typeof i['title'] === 'string' && typeof i['opened_at'] === 'string')
}

export function estDecouvertes(x: unknown): x is DecouvertesSonde {
  return objet(x) && objet(x['counts']) && nombre((x['counts'] as Record<string, unknown>)['pending'])
}

/* ═══════════════ Règles d'affichage ═══════════════ */

const TON_NIV: Record<Niveau, Ton> = { OPERATIONAL: 'ok', ATTENTION: 'warn', DEGRADED: 'err' }

/** Le collecteur de Sonde est muet : tout ce qui suit est une dernière donnée, en gris. */
export const estFige = (c: CollecteurSonde | null | undefined): boolean => !!c && (c.stale || c.ok === false)

/** Appareils dont on ne sait rien de récent : jamais comptés en panne. */
export const inconnus = (c: Comptes): number => (c.unknown ?? 0) + (c.stale ?? 0) + (c.unavailable ?? 0)

/**
 * Couleur d'une salle : son niveau ET son alimentation.
 *   · données figées ou salle sans appareil → gris (on ne sait pas) ;
 *   · coupure secteur, extinction imprévue → rose (une vraie panne) ;
 *   · niveau Attention / Dégradé → ambre / rose ;
 *   · éteinte comme prévu → neutre, JAMAIS rouge ;
 *   · sinon, niveau opérationnel → émeraude ; niveau inconnu → gris.
 */
export function tonSalle(
  z: { level: string | null; power: { situation: string }; counts: { total: number } | null | undefined },
  fige = false,
): Ton {
  if (fige) return 'gris'
  if (z.power.situation === 'mains_outage' || z.power.situation === 'off_unexpected') return 'err'
  if (!z.counts || z.counts.total === 0) return z.power.situation === 'off_as_planned' ? 'off' : 'gris'
  const niv = z.level ? TON_NIV[z.level as Niveau] ?? null : null
  if (niv === 'err' || niv === 'warn') return niv
  if (z.power.situation === 'off_as_planned') return 'off'
  return niv ?? 'gris'
}

/** Couleur du niveau global (ou d'une famille) ; gris si figé ou inconnu. */
export const tonNiveau = (level: string | null | undefined, fige = false): Ton =>
  fige || !level ? 'gris' : TON_NIV[level as Niveau] ?? 'gris'

/** « Pourquoi c'est vert ? » — la couleur dite en clair, telle qu'elle est peinte. */
export function questionPourquoi(t: Ton): string {
  const c: Record<Ton, string> = { ok: 'vert', warn: 'orange', err: 'rouge', off: 'neutre', maint: 'violet', gris: 'gris' }
  return `Pourquoi c’est ${c[t]} ?`
}

/** Les trois salles d'abord, puis l'accueil, le vestiaire, l'extérieur, puis l'ordre de Sonde. */
export const SALLES_PRINCIPALES = ['R1', 'R2', 'R3'] as const
const ENSUITE = ['ACC', 'VEST', 'EXT']

export function ordonnerZones<Z extends { code: string }>(zones: readonly Z[]): { salles: Z[]; autres: Z[] } {
  const salles = SALLES_PRINCIPALES.map((c) => zones.find((z) => z.code === c)).filter((z): z is Z => !!z)
  const reste = zones.filter((z) => !(SALLES_PRINCIPALES as readonly string[]).includes(z.code))
  const rang = (code: string) => { const i = ENSUITE.indexOf(code); return i === -1 ? ENSUITE.length : i }
  const autres = reste.map((z, i) => ({ z, i })).sort((a, b) => rang(a.z.code) - rang(b.z.code) || a.i - b.i).map((x) => x.z)
  return { salles, autres }
}

/**
 * Un chemin produit par Sonde (`AttentionItem.path`) n'est suivi que s'il est
 * relatif et reste dans `/infrastructure`. Tout le reste : pas de lien.
 */
export function cheminInterne(path: string | null | undefined): string | null {
  if (typeof path !== 'string' || path.length > 200) return null
  if (!/^\/infrastructure(?:\/[A-Za-z0-9._:~-]+)*\/?(?:\?[A-Za-z0-9._:~=&%-]*)?$/.test(path)) return null
  if (path.split(/[/?]/).some((s) => s === '..' || s === '.')) return null
  return path
}

/**
 * Ton d'un compteur de confiance `ok/total` (TPE, caméras, ports DMX).
 * `null` → gris (« — ») ; `unobserved` (pas de donnée) n'est jamais une panne.
 */
export function tonCompteur(c: CompteurDirect | null | undefined): Ton {
  if (!c || c.ok === null || c.total === null || c.total === 0) return 'gris'
  const nonVus = c.unobserved ?? 0
  if (c.ok === 0 && nonVus >= c.total) return 'gris'
  return c.total - c.ok - nonVus > 0 ? 'warn' : 'ok'
}

/* ─── Heure de Paris ─── */

const jourParis = (d: Date) => d.toLocaleDateString('fr-CA', { timeZone: 'Europe/Paris' })
const heureParis = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })

function jourRelatif(iso: string, maintenant: Date): string {
  const j = jourParis(new Date(iso))
  if (j === jourParis(maintenant)) return 'aujourd’hui'
  if (j === jourParis(new Date(maintenant.getTime() + 86_400_000))) return 'demain'
  return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', timeZone: 'Europe/Paris' })
}

/**
 * La fenêtre d'exploitation d'une salle, en clair : en cours, sinon la
 * prochaine. `secours` = l'horaire vient du planning de secours de Sonde
 * (Gestion ne lui a pas transmis la soirée).
 */
export function texteFenetre(z: Pick<ZoneSonde, 'window' | 'next_window'>, maintenant = new Date()):
  { titre: string; plage: string; libelle: string | null; secours: boolean; enCours: boolean } | null {
  const f = z.window ?? z.next_window
  if (!f || !f.starts_at || !f.ends_at) return null
  const enCours = !!z.window
  const secours = f.origin === 'fallback'
  const libelle = f.label ?? (secours ? 'Planning de secours' : null)
  return enCours
    ? { titre: 'Soirée en cours', plage: `jusqu’à ${heureParis(f.ends_at)}`, libelle, secours, enCours }
    : { titre: 'Prochaine soirée', plage: `${jourRelatif(f.starts_at, maintenant)} ${heureParis(f.starts_at)} → ${heureParis(f.ends_at)}`, libelle, secours, enCours }
}

/* ─── Domaines du hub : familles de Sonde regroupées ─── */

export const FAMILLES_DOMAINE: Record<'reseau' | 'video' | 'audiovisuel' | 'batiment' | 'serveur', readonly string[]> = {
  reseau: ['network', 'wifi', 'payment', 'pos'],
  video: ['video', 'display'],
  audiovisuel: ['lighting', 'sound', 'dj'],
  batiment: ['power', 'iot', 'fire_safety', 'access'],
  serveur: ['server', 'service'],
}

const POIDS: Record<Niveau, number> = { OPERATIONAL: 0, ATTENTION: 1, DEGRADED: 2 }

/** Agrège les familles d'un domaine : en ligne / total, pire niveau, âge le plus ancien. `null` = rien au registre. */
export function resumeDomaine(familles: readonly FamilleSonde[], codes: readonly string[]):
  { enLigne: number; total: number; level: Niveau | null; attention: number; age: number | null } | null {
  const f = familles.filter((x) => codes.includes(x.family) && x.counts.total > 0)
  if (f.length === 0) return null
  let level: Niveau | null = null
  for (const x of f) if (x.level && (level === null || POIDS[x.level] > POIDS[level])) level = x.level
  const ages = f.map((x) => x.data_age_s).filter((a): a is number => nombre(a))
  return {
    enLigne: f.reduce((s, x) => s + x.counts.online, 0),
    total: f.reduce((s, x) => s + x.counts.total, 0),
    level,
    attention: f.reduce((s, x) => s + (x.attention ?? 0), 0),
    age: ages.length ? Math.max(...ages) : null,
  }
}

/* ─── Incidents ─── */

/** Durée lisible : « 24 min », « 2 h 05 », « 3 j » ; « — » si inconnue. */
export function fmtDuree(s: number | null | undefined): string {
  if (s === null || s === undefined || !Number.isFinite(s) || s < 0) return '—'
  if (s < 60) return `${Math.round(s)} s`
  if (s < 3600) return `${Math.round(s / 60)} min`
  const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60)
  if (h < 48) return m ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`
  return `${Math.round(s / 86_400)} j`
}

const RANG_GRAVITE: Record<string, number> = { critical: 0, error: 1, warning: 2, info: 3 }

/**
 * Incidents « à traiter » du mode soirée : ouverts ou pris en charge, hors
 * ombre (Sonde observe sans alerter), les plus graves puis les plus récents.
 */
export function incidentsATraiter(items: readonly IncidentSonde[], ombre: ReadonlySet<string> = new Set()): IncidentSonde[] {
  return items
    .filter((i) => (i.status === 'open' || i.status === 'acknowledged') && !ombre.has(i.id))
    .sort((a, b) => (RANG_GRAVITE[a.severity] ?? 9) - (RANG_GRAVITE[b.severity] ?? 9) || b.opened_at.localeCompare(a.opened_at))
}

/** Identifiants des incidents que Sonde marque « ombre » dans `attention` (affichés en retrait, jamais notifiés). */
export function incidentsOmbre(o: Pick<OverviewSonde, 'attention'> | null | undefined): Set<string> {
  const s = new Set<string>()
  for (const a of o?.attention.items ?? []) if (a.kind === 'incident' && a.shadow && a.ref) s.add(a.ref)
  return s
}

/* ─── Les 9 sources confiées à Sonde : leurs anciennes fiches restent ouvertes ─── */

export const SOURCES_CONFIEES: readonly { code: string; slug: string; nom: string }[] = [
  { code: 'UNIFI_NETWORK', slug: 'unifi-network', nom: 'UniFi Network' },
  { code: 'UNIFI_PROTECT', slug: 'unifi-protect', nom: 'UniFi Protect' },
  { code: 'HOME_ASSISTANT', slug: 'home-assistant', nom: 'Home Assistant' },
  { code: 'DELL', slug: 'dell', nom: 'Serveur Dell' },
  { code: 'BACKUPS', slug: 'backups', nom: 'Sauvegardes' },
  { code: 'MEDIAMTX', slug: 'mediamtx', nom: 'MediaMTX' },
  { code: 'NPM', slug: 'nginx-proxy-manager', nom: 'Nginx Proxy Manager' },
  { code: 'DOCKER', slug: 'docker', nom: 'Docker' },
  { code: 'ENDPOINTS', slug: 'acces-publics', nom: 'Accès publics' },
]

const CODES_CONFIES = new Set(SOURCES_CONFIEES.map((s) => s.code))

/** Une entrée du catalogue de Gestion qui décrit une des 9 sources techniques (et non un service propre à Gestion). */
export const estSourceConfiee = (e: { source_ref: string | null }): boolean => !!e.source_ref && CODES_CONFIES.has(e.source_ref)
